import { ArrowLeft, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmt } from "@/lib/api";
import { COLOR_BTN, money, unitShort } from "@/components/SaleEditor";
import SyncManager from "@/components/SyncManager";
import { ThemeToggle } from "@/components/ThemeToggle";

export const STATUS = {
  pending: { label: "Onay Bekliyor", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
  approved: { label: "Onaylandı", cls: "bg-green-500/15 text-green-700 dark:text-green-400" },
  rejected: { label: "İptal / Reddedildi", cls: "bg-red-500/15 text-red-700 dark:text-red-400" },
};
export const dt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "-");

export function ModuleHeader({ icon: Icon, title, onBack, testid }) {
  const { logout } = useAuth();
  return (
    <header className="sticky top-0 z-20 bg-card border-b border-border px-3 sm:px-4 pb-3 pt-safe-3 flex items-center gap-2 sm:gap-3">
      <button onClick={onBack} data-testid={`${testid}-back`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0"><ArrowLeft size={16} /> <span className="hidden sm:inline">Modüller</span></button>
      <div className="flex items-center gap-2 font-head font-semibold truncate min-w-0"><Icon size={18} className="text-primary shrink-0" /> <span className="truncate">{title}</span></div>
      <div className="ml-auto flex items-center gap-2 sm:gap-3 shrink-0">
        <SyncManager />
        <ThemeToggle testid={`${testid}-theme-toggle`} />
        <button onClick={logout} data-testid={`${testid}-logout`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"><LogOut size={15} /> <span className="hidden sm:inline">Çıkış</span></button>
      </div>
    </header>
  );
}

export function SaleCard({ sale, children, testid }) {
  const st = STATUS[sale.status] || STATUS.pending;
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="font-head font-semibold text-base">{sale.customer_name}</div>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${st.cls}`} data-testid={`${testid}-status`}>{st.label}</span>
        <div className="text-xs text-muted-foreground ml-auto">{dt(sale.created_at)} · {sale.created_by_name}</div>
      </div>
      <div className="sm:hidden divide-y divide-border">
        {sale.items.map((it) => (
          <div key={it.id} className="px-4 py-2.5 text-sm flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="font-semibold">{["dairy", "extra"].includes(it.breed) ? "" : `${COLOR_BTN[it.breed]?.label || it.breed_label} `}{it.size_label}</div>
              <div className="text-xs text-muted-foreground">{it.source_name} · <span className="tabular-nums">{fmt(it.cartons)} {unitShort(it.unit)} × {money(it.price)}</span></div>
            </div>
            <div className="font-semibold tabular-nums whitespace-nowrap">{money(it.amount ?? it.cartons * it.price)}</div>
          </div>
        ))}
        <div className="px-4 py-2.5 bg-secondary/40 flex justify-between text-sm font-semibold">
          <span>Toplam {fmt(sale.total_cartons)} krt</span>
          <span className="text-primary tabular-nums">{money(sale.total_amount)}</span>
        </div>
      </div>
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Firma / Kaynak</th>
              <th className="text-left px-3 py-2 font-semibold">Çeşit</th>
              <th className="text-left px-3 py-2 font-semibold">Boy</th>
              <th className="text-right px-3 py-2 font-semibold">Miktar</th>
              <th className="text-right px-3 py-2 font-semibold">Birim Fiyat</th>
              <th className="text-right px-3 py-2 font-semibold">Tutar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sale.items.map((it) => (
              <tr key={it.id}>
                <td className="px-3 py-2 font-medium whitespace-nowrap">{it.source_name}</td>
                <td className="px-3 py-2 whitespace-nowrap">{it.breed === "dairy" ? "Süt Ürünü" : it.breed === "extra" ? "Malzeme" : (COLOR_BTN[it.breed]?.label || it.breed_label)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{it.size_label}</td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{fmt(it.cartons)}{it.unit && it.unit !== "karton" ? ` ${it.unit}` : ""}</td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{money(it.price)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold whitespace-nowrap">{money(it.amount ?? it.cartons * it.price)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-secondary/40 font-semibold">
            <tr>
              <td className="px-3 py-2" colSpan={3}>Toplam</td>
              <td className="px-3 py-2 text-right tabular-nums" data-testid={`${testid}-cartons`}>{fmt(sale.total_cartons)}</td>
              <td />
              <td className="px-3 py-2 text-right tabular-nums text-primary whitespace-nowrap" data-testid={`${testid}-amount`}>{money(sale.total_amount)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {(sale.status === "approved" || sale.status === "rejected") && (
        <div className="px-4 py-2 text-xs text-muted-foreground border-t border-border">
          {sale.status === "approved"
            ? <>Onaylayan: {sale.approved_by_name} · {dt(sale.approved_at)}{sale.forced && <span className="text-amber-600"> · stok yetersizken onaylandı</span>}</>
            : <>İptal eden: {sale.rejected_by_name} · {dt(sale.rejected_at)}{sale.reject_reason && ` · Sebep: ${sale.reject_reason}`}</>}
        </div>
      )}
      {children && <div className="px-4 py-3 border-t border-border flex flex-wrap gap-2 justify-end">{children}</div>}
    </div>
  );
}
